import { useState, useEffect, useCallback, useRef } from 'react';

export default function VinculacionHeader({
    logoUrls = [],
    logoFallback,
    companyName, 
    formIcon,
    formTitle
}) {
    const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
    const [useFallback, setUseFallback] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [imageKey, setImageKey] = useState(0);
    const imgRef = useRef(null);

    // Determinar qué logo mostrar
    const logoSrc = useFallback || logoUrls.length === 0 
        ? logoFallback 
        : logoUrls[currentLogoIndex];

    // Manejar error de carga de imagen
    const handleImageError = useCallback(() => {
        // Si hay más URLs por intentar
        if (currentLogoIndex < logoUrls.length - 1) {
            setCurrentLogoIndex(prev => prev + 1);
            setImageKey(prev => prev + 1);
        } else {
            // Si no hay más URLs, usar fallback
            setUseFallback(true);
            setIsLoading(false);
        }
    }, [currentLogoIndex, logoUrls.length]);

    // Manejar carga exitosa de imagen
    const handleImageLoad = useCallback((e) => {
        // Verificar que la imagen realmente se cargó
        if (e?.target?.complete && e.target.naturalHeight !== 0) {
            setIsLoading(false);
        }
    }, []);

    // Reset del estado de carga cuando cambian las URLs
    useEffect(() => {
        if (logoUrls.length > 0 || logoFallback) {
            setIsLoading(true);
            setCurrentLogoIndex(0);
            setUseFallback(false);
            setImageKey(prev => prev + 1);
        }
    }, [logoUrls, logoFallback]);

    // Verificar si la imagen ya está en caché o cargarla
    useEffect(() => {
        if (!logoSrc) return;

        const img = new Image();
        let isMounted = true;

        const handleLoad = () => {
            if (isMounted) {
                setIsLoading(false);
            }
        };

        const handleError = () => {
            if (isMounted) {
                handleImageError();
            }
        };

        img.onload = handleLoad;
        img.onerror = handleError;
        img.src = logoSrc;

        // Si la imagen ya está en caché, se marca como completa inmediatamente
        if (img.complete && img.naturalHeight !== 0) {
            setIsLoading(false);
        }

        return () => {
            isMounted = false;
            img.onload = null;
            img.onerror = null;
        };
    }, [logoSrc, handleImageError]);

    return (
        <>
            <div className="welcome-header">
                <div className="welcome-header__logo-container">
                    {isLoading && (
                        <div className="logo-spinner">
                            <div className="spinner"></div>
                            <span className="logo-spinner__text">Cargando logo...</span>
                        </div>
                    )}
                    <img
                        ref={imgRef}
                        key={`logo-${imageKey}`}
                        src={logoSrc}
                        alt={`Logo de ${companyName}`}
                        className={`welcome-header__logo ${isLoading ? 'logo-hidden' : 'logo-visible'}`}
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                        style={{ display: isLoading ? 'none' : 'block' }}
                    />
                </div>
                <h1 className="welcome-header__text">
                    {companyName}
                </h1>
            </div>
            <div className="welcome-subtext">
                <p>Bienvenido al portal de vinculación.</p>
                <p>Completa todos los campos obligatorios "*" del formulario para registrar correctamente la información.</p>
            </div>
            <h2 className="form-title mt-4">
                <i className="material-icons">{formIcon}</i> {formTitle}
            </h2>
        </>
    );
}